/*eslint max-lines-per-function: ["error", 200]*/
import React from 'react';
import {
  // eslint-disable-next-line import/named
  Auth,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateEmail,
  updatePassword,
  // eslint-disable-next-line import/named
  UserCredential,
} from 'firebase/auth';
import type { IdTokenResult } from 'firebase/auth';

import { User } from './spdCore/contexts/UserContext';
import { Role } from './spdCore/authorization/roles';

export interface SpdUser {
  uid: string;
  email: string;
  name: string;
  id: string;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export enum AuthState {
  Unknown,
  LoggedIn,
  LoggedOut,
  TwoFactor,
}

interface AuthProviderState {
  state: AuthState;
  currentUser?: SpdUser;
  roles: Role[] | undefined;
  userProfile?: User;
  expirationTime?: string | undefined;
  ensureTokenIsValid: () => Promise<void>;
  logout: () => Promise<void>;
  initTwoFactor: () => Promise<void>;
  verifyTwoFactor: (code: string) => Promise<void>;
  updateProfile: (uzytkownik?: User) => Promise<boolean>;
  updateEmailCurrentUser: () => boolean;
  check: (roles: Role[]) => boolean | undefined;
  updatePin: (pin: string) => void;
  canSign: boolean;
}

const unknownState = {
  state: AuthState.Unknown,
  currentUser: undefined,
  userProfile: undefined,
  roles: undefined,
  expirationTime: undefined,
  ensureTokenIsValid: async () => {},
  logout: () => Promise.resolve(undefined),
  initTwoFactor: async () => {},
  verifyTwoFactor: async () => {},
  updateProfile: () => Promise.resolve(false),
  updateEmailCurrentUser: () => false,
  check: () => undefined,
  updatePin: () => undefined,
  canSign: false,
};

export const AuthContext = React.createContext<AuthProviderState>(unknownState);

export class AuthProvider extends React.Component<
  AuthProviderProps,
  AuthProviderState
> {
  public static readonly TOKEN_KEY = 'mzt-token';
  public static readonly TOKEN_EXPIRATION_KEY = 'mzt-token-expiration-time';
  public static readonly TWO_FACTOR_TOKEN_KEY = 'mzt-2fa-token';
  public static readonly TOKEN_REFRESH_BUFFER_MS = 55 * 60 * 1000; // 55 minutes

  private unsubscribeToken: (() => void) | null = null;
  private unsubscribeAuth: (() => void) | null = null;
  private tokenRefreshInProgress = false;
  private tfaRequiredPending = false;
  private handleTfaRequired: (() => void) | null = null;
  private handleSessionInactive: (() => void) | null = null;
  private handleStorageChange: ((e: StorageEvent) => void) | null = null;

  constructor(props: AuthProviderProps) {
    super(props);
    this.state = unknownState;
  }

  handleTokenResult(token: IdTokenResult) {
    localStorage.setItem(AuthProvider.TOKEN_KEY, token.token);
    localStorage.setItem(
      AuthProvider.TOKEN_EXPIRATION_KEY,
      token.expirationTime,
    );
  }

  async tokenRefresh(authFirebase: Auth) {
    try {
      const user = authFirebase.currentUser;
      if (user) {
        const result = await user.getIdTokenResult(true);
        this.handleTokenResult(result);
      }
    } catch (error) {
      console.log(`Wystąpił problem z pobraniem tokenu. ${error}`);
    }
  }

  private async execUpdateProfile(
    state: AuthProviderState,
    profile: User | undefined,
  ): Promise<boolean> {
    try {
      if (profile) {
        const authFirebaseUP = getAuth();
        const newState = state;
        newState.userProfile = profile;
        newState.canSign = profile?.pin !== undefined;
        newState.roles = [];
        if (!profile?.active) {
          newState.roles.push(Role.Inactive);
        } else {
          if (profile?.roles) {
            newState.roles.push(...(profile?.roles as Role[]));
          }
          if (profile?.admin && !newState.roles.includes(Role.Admin)) {
            newState.roles.push(Role.Admin);
          }
          if (
            profile?.superadmin &&
            !newState.roles.includes(Role.SuperAdmin)
          ) {
            newState.roles.push(Role.SuperAdmin);
          }
        }
        await this.tokenRefresh(authFirebaseUP);
        this.setState(newState);
        return true;
      }
      return false;
    } catch (error) {
      console.log(
        `Wystąpił problem z pobraniem tokenu i aktualizacją profilu. ${error}`,
      );
      return false;
    }
  }

  private async execEnsureTokenIsValid(): Promise<void> {
    const authFirebase2 = getAuth();
    const expirationTime: string | null = localStorage.getItem(
      AuthProvider.TOKEN_EXPIRATION_KEY,
    );
    if (expirationTime !== null) {
      try {
        const expirationDate = new Date(expirationTime);
        const refreshThreshold =
          expirationDate.getTime() - AuthProvider.TOKEN_REFRESH_BUFFER_MS;
        if (refreshThreshold > new Date().getTime()) {
          return;
        }
      } catch (error) {
        console.log(
          `Wystąpił problem z parsowaniem daty wygaśnięcia tokenu. ${error}`,
        );
      }
    }
    if (this.tokenRefreshInProgress) {
      await new Promise(resolve => setTimeout(resolve, 100));
      return;
    }
    try {
      this.tokenRefreshInProgress = true;
      await this.tokenRefresh(authFirebase2);
    } finally {
      this.tokenRefreshInProgress = false;
    }
  }

  private requiresTwoFactor(claims: Record<string, unknown>): boolean {
    const twoFactorEnabled = claims['twoFactorEnabled'] as boolean | undefined;
    const hasTwoFactorSession = !!localStorage.getItem(
      AuthProvider.TWO_FACTOR_TOKEN_KEY,
    );
    return !!twoFactorEnabled && !hasTwoFactorSession;
  }

  componentDidMount() {
    const authFirebase = getAuth();
    const state: AuthProviderState = unknownState;

    state.logout = async () => {
      const token = localStorage.getItem(AuthProvider.TOKEN_KEY);
      const tfaToken = localStorage.getItem(AuthProvider.TWO_FACTOR_TOKEN_KEY);
      if (token && tfaToken) {
        fetch(`${process.env.REACT_APP_SPDAPI_URL}/auth/2fa/session`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-2FA-Session': tfaToken,
          },
        }).catch(() => {});
      }
      localStorage.removeItem(AuthProvider.TOKEN_KEY);
      localStorage.removeItem(AuthProvider.TOKEN_EXPIRATION_KEY);
      localStorage.removeItem(AuthProvider.TWO_FACTOR_TOKEN_KEY);
      sessionStorage.clear();
      state.roles = [];
      return signOut(getAuth());
    };

    state.initTwoFactor = async () => {
      await this.execEnsureTokenIsValid();
      const token = localStorage.getItem(AuthProvider.TOKEN_KEY);
      const response = await fetch(
        `${process.env.REACT_APP_SPDAPI_URL}/auth/2fa/init`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.ok) {
        throw new Error('Nie udało się wysłać kodu weryfikacyjnego.');
      }
    };

    state.verifyTwoFactor = async (code: string) => {
      await this.execEnsureTokenIsValid();
      const token = localStorage.getItem(AuthProvider.TOKEN_KEY);
      const response = await fetch(
        `${process.env.REACT_APP_SPDAPI_URL}/auth/2fa/verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Nieprawidłowy kod weryfikacyjny.');
      }
      const data = await response.json();
      localStorage.setItem(
        AuthProvider.TWO_FACTOR_TOKEN_KEY,
        data.sessionToken,
      );
      this.tfaRequiredPending = false;
      state.state = AuthState.LoggedIn;
      this.setState({ ...state });
    };

    state.updateProfile = profile => this.execUpdateProfile(state, profile);

    state.updateEmailCurrentUser = () => {
      const authFirebase = getAuth();
      const user = authFirebase.currentUser;

      if (user) {
        state.state = AuthState.LoggedIn;

        state.currentUser = {
          uid: user?.uid,
          email: user?.email,
          name: user?.displayName,
        } as SpdUser;

        this.setState(state);
      }
      return true;
    };

    state.updatePin = (pin: string) => {
      const authFirebase = getAuth();
      const user = authFirebase.currentUser;

      if (user) {
        state.userProfile = {
          ...state.userProfile,
          pin,
        } as User;
        this.setState(state);
      }
    };

    state.check = (roles: Role[]) => {
      if (state.roles === undefined) {
        return undefined;
      }
      return state.roles.some(item => roles.includes(item));
    };

    state.ensureTokenIsValid = () => this.execEnsureTokenIsValid();

    this.handleTfaRequired = () => {
      if (this.tfaRequiredPending) {
        return;
      }
      this.tfaRequiredPending = true;
      localStorage.removeItem(AuthProvider.TWO_FACTOR_TOKEN_KEY);
      state.state = AuthState.TwoFactor;
      this.setState({ ...state });
    };
    window.addEventListener('tfa-required', this.handleTfaRequired);

    this.handleSessionInactive = () => {
      const returnTo = window.location.pathname + window.location.search;
      void state.logout().finally(() => {
        // sessionStorage.clear() already ran inside logout() — safe to write now
        sessionStorage.setItem('forced_logout_return_to', returnTo);
        window.location.href = '/login';
      });
    };
    window.addEventListener('session-inactive', this.handleSessionInactive);

    this.handleStorageChange = (e: StorageEvent) => {
      if (e.key !== AuthProvider.TWO_FACTOR_TOKEN_KEY) {
        return;
      }
      if (e.newValue === null && state.state === AuthState.LoggedIn) {
        // another tab cleared the 2FA token → show 2FA screen here too
        this.tfaRequiredPending = true;
        state.state = AuthState.TwoFactor;
        this.setState({ ...state });
      } else if (e.newValue && state.state === AuthState.TwoFactor) {
        // another tab verified → update this tab to LoggedIn
        this.tfaRequiredPending = false;
        state.state = AuthState.LoggedIn;
        this.setState({ ...state });
      }
    };
    window.addEventListener('storage', this.handleStorageChange);

    this.setState(state);

    this.unsubscribeToken = authFirebase.onIdTokenChanged(async user => {
      const result = await user?.getIdTokenResult();
      if (result) {
        this.handleTokenResult(result);
        if (this.requiresTwoFactor(result.claims)) {
          state.state = AuthState.TwoFactor;
          this.setState({ ...state });
        }
      }
    });

    this.unsubscribeAuth = authFirebase.onAuthStateChanged(async user => {
      try {
        if (user) {
          const tokenResult = await user.getIdTokenResult();
          this.handleTokenResult(tokenResult);

          state.currentUser = {
            uid: user.uid,
            email: user.email,
            name: user.displayName,
          } as SpdUser;

          state.state = this.requiresTwoFactor(tokenResult.claims)
            ? AuthState.TwoFactor
            : AuthState.LoggedIn;
        } else {
          state.state = AuthState.LoggedOut;
          state.userProfile = undefined;
          state.currentUser = undefined;
        }
      } catch (error) {
        console.log(`Wystąpił problem z pobraniem tokenu. ${error}`);
        state.state = AuthState.LoggedOut;
        state.userProfile = undefined;
        state.currentUser = undefined;
      }
      this.setState(state);
    });
  }

  componentWillUnmount(): void {
    if (this.handleTfaRequired) {
      window.removeEventListener('tfa-required', this.handleTfaRequired);
    }
    if (this.handleSessionInactive) {
      window.removeEventListener(
        'session-inactive',
        this.handleSessionInactive,
      );
    }
    if (this.handleStorageChange) {
      window.removeEventListener('storage', this.handleStorageChange);
    }
    if (this.unsubscribeToken) {
      this.unsubscribeToken();
    }
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }

  render() {
    return (
      <AuthContext.Provider value={this.state}>
        {this.props.children}
      </AuthContext.Provider>
    );
  }

  static async createUserWithEmailAndPassword(
    email: string,
    password: string,
  ): Promise<UserCredential | void> {
    const authFirebase = getAuth();
    const result = await createUserWithEmailAndPassword(
      authFirebase,
      email,
      password,
    );
    return result;
  }

  static signInWithEmailAndPassword(
    username: string,
    password: string,
  ): Promise<UserCredential> {
    const authFirebase = getAuth();
    return signInWithEmailAndPassword(authFirebase, username, password);
  }

  static resetPassword(email: string) {
    const authFirebase = getAuth();
    return sendPasswordResetEmail(authFirebase, email);
  }

  static updateEmail(email?: string) {
    const currentUser = getAuth().currentUser;
    if (!email || !currentUser) {
      return Promise.resolve();
    }
    return updateEmail(currentUser, email);
  }

  static updatePassword(pass: string) {
    const currentUser = getAuth().currentUser;
    if (!pass || !currentUser) {
      return Promise.resolve();
    }
    return updatePassword(currentUser, pass);
  }

  static reauthenticateWithCredential(currentPass: string) {
    const currentUser = getAuth().currentUser;
    if (!currentPass || !currentUser || !currentUser.email) {
      return Promise.resolve();
    }
    const credential = EmailAuthProvider.credential(
      currentUser.email,
      currentPass,
    );
    return reauthenticateWithCredential(currentUser, credential);
  }
}
